import { useState, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { DragEndEvent } from '@dnd-kit/core';
import { RankingEntity, Team } from '../components/SortableEntity';

export const useRankingLogic = () => {
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null);

  // Recalculate all rankings and positions properly
  const recalculateRankings = useCallback((entities: RankingEntity[]) => {
    // Sort entities by their original position to maintain order
    const sortedEntities = [...entities].sort((a, b) => a.position - b.position);
    
    // Build tie groups using a simpler approach
    const processedIds = new Set<string>();
    const tieGroups: RankingEntity[][] = [];
    
    for (const entity of sortedEntities) {
      if (processedIds.has(entity.id)) continue;
      
      if (entity.tiedWith.length === 0) {
        // Single entity, not tied with anyone
        tieGroups.push([entity]);
        processedIds.add(entity.id);
      } else {
        // Find all entities that should be in this tie group
        const tieGroup = [entity];
        processedIds.add(entity.id);
        
        // Add all directly tied entities
        for (const tiedId of entity.tiedWith) {
          const tiedEntity = sortedEntities.find(e => e.id === tiedId);
          if (tiedEntity && !processedIds.has(tiedId)) {
            tieGroup.push(tiedEntity);
            processedIds.add(tiedId);
          }
        }
        
        // Now check if any of the tied entities have additional ties we need to include
        // (This handles transitive ties: if A ties with B, and B ties with C, then A-B-C are all tied)
        let foundNewTies = true;
        while (foundNewTies) {
          foundNewTies = false;
          const currentGroupIds = tieGroup.map(e => e.id);
          
          for (const groupEntity of tieGroup) {
            for (const tiedId of groupEntity.tiedWith) {
              if (!currentGroupIds.includes(tiedId) && !processedIds.has(tiedId)) {
                const additionalEntity = sortedEntities.find(e => e.id === tiedId);
                if (additionalEntity) {
                  tieGroup.push(additionalEntity);
                  processedIds.add(tiedId);
                  foundNewTies = true;
                }
              }
            }
          }
        }
        
        // Ensure all entities in the group have consistent tiedWith arrays
        const allGroupIds = tieGroup.map(e => e.id);
        tieGroup.forEach(entity => {
          entity.tiedWith = allGroupIds.filter(id => id !== entity.id);
        });
        
        tieGroups.push(tieGroup);
      }
    }
    
    // Assign positions to each group
    const finalEntities: RankingEntity[] = [];
    let currentPosition = 1;
    
    for (const group of tieGroups) {
      // All entities in this group get the same position
      for (const entity of group) {
        finalEntities.push({
          ...entity,
          position: currentPosition
        });
      }
      
      // Next position skips by the number of tied entities
      currentPosition += group.length;
    }
    
    return finalEntities;
  }, []);

  // Update ranking entities when teams or players change
  const updateRankingEntities = useCallback((teams: Team[], selectedPlayerIds: string[]) => {
    const entities: RankingEntity[] = [];
    let position = 1;

    // Add teams first
    teams.forEach(team => {
      entities.push({
        id: team.id,
        type: 'team',
        position: position++,
        tiedWith: []
      });
    });

    // Add individual players (not in teams)
    selectedPlayerIds.forEach(playerId => {
      const isInTeam = teams.some(team => team.playerIds.includes(playerId));
      if (!isInTeam) {
        entities.push({
          id: playerId,
          type: 'player',
          position: position++,
          tiedWith: []
        });
      }
    });

    return entities;
  }, []);

  const handleDragStart = useCallback((event: any) => {
    console.log('Drag started:', event.active.id);
  }, []);

  const handleDragOver = useCallback((event: any) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setDraggedOverId(over.id as string);
    } else {
      setDraggedOverId(null);
    }
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent, setRankingEntities: (updater: (prev: RankingEntity[]) => RankingEntity[]) => void) => {
    const { active, over } = event;
    setDraggedOverId(null);

    if (!over) return;

    if (active.id !== over.id) {
      setRankingEntities((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        
        // Regular reorder - move items and reset all ties
        const reorderedItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({
          ...item,
          position: index + 1,
          tiedWith: [], // Reset all ties on reorder for simplicity
        }));
        
        // Recalculate rankings to ensure consistency
        return recalculateRankings(reorderedItems);
      });
    }
  }, [recalculateRankings]);

  // Create ties manually
  const createTie = useCallback((entityId1: string, entityId2: string, setRankingEntities: (updater: (prev: RankingEntity[]) => RankingEntity[]) => void) => {
    setRankingEntities(prev => {
      const entity1 = prev.find(p => p.id === entityId1);
      const entity2 = prev.find(p => p.id === entityId2);
      
      if (!entity1 || !entity2) return prev;
      
      const targetPosition = Math.min(entity1.position, entity2.position);
      
      // Update entities to create the tie
      const updatedEntities = prev.map(item => {
        if (item.id === entityId1) {
          return {
            ...item,
            position: targetPosition,
            tiedWith: Array.from(new Set([...item.tiedWith, entityId2]))
          };
        }
        if (item.id === entityId2) {
          return {
            ...item,
            position: targetPosition,
            tiedWith: Array.from(new Set([...item.tiedWith, entityId1]))
          };
        }
        return item;
      });
      
      // Recalculate all rankings to ensure consistency
      return recalculateRankings(updatedEntities);
    });
  }, [recalculateRankings]);

  const separateFromTie = useCallback((entityId: string, setRankingEntities: (updater: (prev: RankingEntity[]) => RankingEntity[]) => void) => {
    setRankingEntities(prev => {
      const entityToSeparate = prev.find(p => p.id === entityId);
      if (!entityToSeparate || entityToSeparate.tiedWith.length === 0) return prev;

      // Remove the entity from all tie relationships
      const entitiesWithoutTies = prev.map(entity => {
        if (entity.id === entityId) {
          return { ...entity, tiedWith: [] };
        }
        if (entity.tiedWith.includes(entityId)) {
          return { ...entity, tiedWith: entity.tiedWith.filter(id => id !== entityId) };
        }
        return entity;
      });

      // Recalculate all rankings to ensure consistency
      return recalculateRankings(entitiesWithoutTies);
    });
  }, [recalculateRankings]);

  return {
    draggedOverId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    createTie,
    separateFromTie,
    updateRankingEntities,
    recalculateRankings
  };
};